import { useState, type FormEvent } from "react";
import { useNavigate, Link } from "react-router";
import { Clock } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { apiFetch } from "../api/client";

export function Register() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const regRes = await apiFetch("/auth/register", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });
      if (!regRes.ok) {
        const data = await regRes.json().catch(() => ({}));
        setError((data as { detail?: string }).detail ?? "Registration failed");
        return;
      }
      const loginRes = await apiFetch("/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });
      const { token } = await loginRes.json();
      const meRes = await fetch("/api/auth/me", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!meRes.ok) {
        setError("Could not load account. Please try again.");
        return;
      }
      const user = await meRes.json();
      login(token, { id: String(user.id), email: String(user.email) });
      navigate("/");
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-canvas overflow-hidden relative">
      <div className="pointer-events-none absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-amber/15 blur-[120px]" />
      <div className="pointer-events-none absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-brand/20 blur-[120px]" />

      <div className="relative w-full max-w-sm space-y-6 px-4">
        <div className="text-center space-y-2">
          <Clock size={48} className="text-brand mx-auto" strokeWidth={1.5} />
          <h1 className="text-3xl font-bold font-display bg-gradient-to-r from-brand to-amber bg-clip-text text-transparent">
            TimeCraftr
          </h1>
          <p className="text-ink-3 text-sm">Track time. Stay focused.</p>
        </div>
        <form
          onSubmit={handleSubmit}
          className="bg-card/80 backdrop-blur-xl border border-white/[0.08] rounded-2xl p-8 space-y-4 shadow-2xl"
        >
          <h2 className="text-xl font-semibold text-ink">Create account</h2>
          {error && (
            <p className="text-red-400 text-sm bg-red-400/10 rounded-lg px-3 py-2">
              {error}
            </p>
          )}
          <div>
            <label className="block text-sm text-ink-2 mb-1">Email</label>
            <input
              type="email"
              name="email-timecraftr"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-field border border-white/[0.08] rounded-lg px-3 py-2 text-ink text-sm focus:outline-none focus:border-brand transition-colors"
            />
          </div>
          <div>
            <label className="block text-sm text-ink-2 mb-1">Password</label>
            <input
              type="password"
              name="password-timecraftr"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-field border border-white/[0.08] rounded-lg px-3 py-2 text-ink text-sm focus:outline-none focus:border-brand transition-colors"
            />
          </div>
          <div>
            <label className="block text-sm text-ink-2 mb-1">Confirm password</label>
            <input
              type="password"
              name="confirm-timecraftr"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full bg-field border border-white/[0.08] rounded-lg px-3 py-2 text-ink text-sm focus:outline-none focus:border-brand transition-colors"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-brand hover:bg-brand-2 text-white rounded-lg py-2.5 text-sm font-medium disabled:opacity-50 transition-colors"
          >
            {loading ? "Creating account…" : "Create account"}
          </button>
          <p className="text-center text-sm text-ink-3">
            Already have an account?{" "}
            <Link to="/login" className="text-brand hover:opacity-80 transition-opacity">
              Sign in
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
